import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSchools } from "../hooks/useSchools";
import { useReviews } from "../hooks/useReviews";
import { useUsers } from "../hooks/useUsers";
import { useAuthStore } from "../store/authStore";
import SchoolCard from "../components/SchoolCard";

export default function HomePage() {
  const { isAuthenticated } = useAuthStore();
  const { fetchSchools } = useSchools();
  const { fetchReviews } = useReviews();
  const { getUserCount } = useUsers();

  const [featuredSchools, setFeaturedSchools] = useState([]);
  const [stats, setStats] = useState({
    totalSchools: 0,
    totalReviews: 0,
    totalUsers: 0,
  });

  const loadAllData = async () => {
    // Tải dữ liệu từ cả 3 nguồn
    try {
      const [schoolsResult, reviewsResult, usersResult] = await Promise.all([
        fetchSchools({
          limit: 6,
          sortBy: "rating",
          sortOrder: "desc",
        }),
        fetchReviews({ limit: 1 }),
        getUserCount(),
      ]);

      // Xử lý kết quả từ API schools
      if (schoolsResult?.success !== false) {
        // Đảm bảo mỗi trường học có ID duy nhất
        const schoolsWithValidIds =
          schoolsResult?.schools?.map((school, index) =>
            school.id ? school : { ...school, id: `temp-id-${index}` }
          ) || [];

        setFeaturedSchools(schoolsWithValidIds);

        // Cập nhật thống kê
        setStats((prevStats) => ({
          ...prevStats,
          totalSchools: schoolsResult?.total || 0,
        }));
      }

      // Xử lý kết quả từ API reviews
      if (reviewsResult?.success !== false) {
        setStats((prevStats) => ({
          ...prevStats,
          totalReviews: reviewsResult?.total || 0,
        }));
      }

      // Xử lý kết quả từ API users count
      if (usersResult?.success !== false) {
        setStats((prevStats) => ({
          ...prevStats,
          totalUsers: usersResult?.totalUsers || 0,
        }));
      }
    } catch (err) {
      console.error("Error loading homepage data:", err);
    }
  };

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="home-page__hero">
        <div className="container">
          <div className="home-page__hero-content">
            <h1>Tìm trường học phù hợp</h1>
            <p>
              Khám phá và đánh giá các trường học tại Việt Nam. Chia sẻ trải
              nghiệm, đọc đánh giá từ cộng đồng để đưa ra lựa chọn giáo dục tốt
              nhất.
            </p>

            <div className="home-page__hero-actions">
              <Link to="/schools" className="btn btn--primary">
                Khám phá trường học
              </Link>
              {!isAuthenticated && (
                <Link to="/register" className="btn btn--secondary">
                  Tham gia ngay
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="home-page__features">
        <div className="container">
          <div className="home-page__features-header">
            <h2>Tại sao chọn Review Trường?</h2>
            <p>Nền tảng đánh giá trường học toàn diện và đáng tin cậy</p>
          </div>

          <div className="home-page__features-grid">
            <div key="feature-search" className="home-page__features-item">
              <div className="home-page__features-item-icon">
                <svg
                  className="icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <h3>Tìm kiếm dễ dàng</h3>
              <p>
                Tìm kiếm trường học theo tên, địa điểm, cấp học với giao diện
                thân thiện
              </p>
            </div>

            <div key="feature-rating" className="home-page__features-item">
              <div className="home-page__features-item-icon">
                <svg
                  className="icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                  />
                </svg>
              </div>
              <h3>Đánh giá chi tiết</h3>
              <p>
                Hệ thống đánh giá 4 tiêu chí: Giảng dạy, Cơ sở vật chất, Hoạt
                động, Môi trường
              </p>
            </div>

            <div key="feature-community" className="home-page__features-item">
              <div className="home-page__features-item-icon">
                <svg
                  className="icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <h3>Cộng đồng thảo luận</h3>
              <p>
                Tham gia thảo luận, chia sẻ kinh nghiệm với cộng đồng phụ huynh
                và học sinh
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="home-page__stats">
        <div className="container">
          <div className="home-page__section-title">
            <h2>Thống kê hệ thống</h2>
          </div>

          <div className="home-page__stats-grid">
            <div key="stat-schools" className="home-page__stats-item">
              <h3>{stats.totalSchools}+</h3>
              <p>Trường học</p>
            </div>
            <div key="stat-reviews" className="home-page__stats-item">
              <h3>{stats.totalReviews}+</h3>
              <p>Đánh giá</p>
            </div>
            <div key="stat-users" className="home-page__stats-item">
              <h3>{stats.totalUsers}+</h3>
              <p>Người dùng</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Schools */}
      {featuredSchools.length > 0 && (
        <section className="home-page__featured-schools">
          <div className="container">
            <div className="home-page__featured-schools-header">
              <h2>Trường học nổi bật</h2>
              <p>Khám phá các trường học được đánh giá cao</p>
            </div>

            <div className="home-page__featured-schools-grid">
              {featuredSchools.map((school) => (
                <SchoolCard
                  key={school.id}
                  school={{
                    ...school,
                    rating: school.ratings?.overall || 0,
                  }}
                  onClick={() =>
                    (window.location.href = `/schools/${school._id}`)
                  }
                />
              ))}
            </div>

            <div className="home-page__featured-schools-footer">
              <Link to="/schools" className="btn btn--secondary">
                Xem tất cả trường học
                <svg
                  className="icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="home-page__cta">
        <div className="container">
          <h2>Bắt đầu hành trình tìm trường ngay hôm nay</h2>
          <p>
            Tham gia cộng đồng để chia sẻ và nhận được những thông tin hữu ích
            nhất
          </p>

          <div className="home-page__cta-actions">
            <Link to="/schools" className="btn btn--primary">
              Tìm trường ngay
            </Link>
            {isAuthenticated ? (
              <Link to="/schools/create" className="btn btn--secondary">
                Thêm trường mới
              </Link>
            ) : (
              <Link to="/register" className="btn btn--secondary">
                Đăng ký tài khoản
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
